import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {
  createDefaultFacet,
  DATA_TYPES,
  DataType,
  FACET_TYPE_LABELS,
  FACET_TYPES,
  FacetType,
  getValueFieldDisplay,
  IDSFacet,
  IDSValueField,
  isValueFieldEmpty,
  NECESSITY_OPTIONS,
  NecessityType,
  PROPERTY_DATA_TYPES,
  RELATIONS
} from '../models/ids.model';
import {
  dataTypeGroupMemberList,
  dataTypeGroupMembers,
  dataTypeGroupOf,
  dataTypeGroupTooltipTitle,
  IFC_DATA_TYPE_GROUPS,
  IfcDataTypeGroup,
  knownDataTypes,
} from '../models/ifc-data-type-groups';
import {
  entityListTooltip,
  groupMembers,
  IFC_ENTITY_GROUPS,
  IfcEntityGroup,
  matchEntityGroup,
  subtypesOf,
} from '../models/ifc-entity-groups';
import {FormsModule} from '@angular/forms';
import {NgTemplateOutlet} from '@angular/common';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faCopy,
  faEllipsisV,
  faPen,
  faPlus,
  faTrash,
  faXmark
} from '@fortawesome/pro-solid-svg-icons';
import {
  BicBadgeComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  BicInputComponent,
  BicSelectComponent,
  TooltipDirective
} from '@buildinginformationcloud/bic-ui-components';
import {ChangeMarkerComponent} from '../../../../../util/design/change-marker/change-marker.component';
import {ProposalActionsComponent} from '../../../../../util/design/proposal-actions/proposal-actions.component';
import {MyCheckFieldChange} from '../../../../../ruleManagement/proposal/mycheck-diff';

@Component({
  selector: 'app-ids-facet',
  templateUrl: './ids-facet.component.html',
  imports: [FormsModule, NgTemplateOutlet, FaIconComponent, BicSelectComponent, BicDropdownElementComponent, BicInputComponent, BicBadgeComponent, BicDropdownComponent, BicDropdownTriggerDirective, TooltipDirective, ChangeMarkerComponent, ProposalActionsComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class IdsFacetComponent implements OnChanges {
  @Input() facet!: IDSFacet;
  /**
   * IFC-Versionen der Spezifikation, zu der diese Facette gehört.
   *
   * Nötig für die Entitätengruppen und die Subklassen-Auflösung: `IfcBuildingElement` existiert in
   * IFC4X3 nicht mehr, `IfcSpatialElement` nicht in IFC2X3. Ohne die Versionen wäre die
   * Mitgliederliste einer Gruppe schlicht falsch.
   */
  @Input() ifcVersions: string[] = [];
  @Input() isRequirement: boolean = false;
  @Input() submitted = false;
  /** Änderungen eines chatBIC-Vorschlags an dieser Facette, Feldname (`propertyValue`, …) → Vorher/Nachher. */
  @Input() changes?: Map<string, MyCheckFieldChange>;
  /** Übernehmen/Verwerfen eines einzelnen Facettenfelds, mit dem Feldnamen. */
  @Output() changeAccepted = new EventEmitter<string>();
  @Output() changeRejected = new EventEmitter<string>();
  /** ✓/✗ an der ganzen Facette – betrifft nur diese Facette, nicht die übrigen der Liste. */
  @Output() facetAccepted = new EventEmitter<void>();
  @Output() facetRejected = new EventEmitter<void>();
  @Output() facetChange = new EventEmitter<IDSFacet>();
  @Output() remove = new EventEmitter<void>();
  @Output() duplicate = new EventEmitter<void>();

  protected readonly faChevronDown = faChevronDown;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faEllipsisV = faEllipsisV;
  protected readonly faPlus = faPlus;
  protected readonly faXmark = faXmark;
  protected readonly faPen = faPen;
  protected readonly faCopy = faCopy;
  protected readonly faTrash = faTrash;

  protected readonly DATA_TYPES = DATA_TYPES;
  protected readonly PROPERTY_DATA_TYPES = PROPERTY_DATA_TYPES;
  protected readonly RELATIONS = RELATIONS;
  protected readonly IFC_ENTITY_GROUPS = IFC_ENTITY_GROUPS;
  protected readonly FACET_TYPE_LABELS = FACET_TYPE_LABELS;
  protected readonly NECESSITY_OPTIONS = NECESSITY_OPTIONS;

  /** Blendet „Entitätengruppe" bei Anforderungen aus - dort nicht sinnvoll wählbar. */
  get visibleFacetTypes(): typeof FACET_TYPES {
    return this.isRequirement ? FACET_TYPES.filter(ft => !ft.applicabilityOnly) : FACET_TYPES;
  }

  /**
   * Entity-Anforderungen können ihre Necessity nicht exportieren: das IDS-1.0-Schema erlaubt bei
   * <ids:entity> in Anforderungen kein cardinality-Attribut (siehe IdsAdapterService.entityReqAttrs)
   * - ein Export würde sonst entweder die Necessity stillschweigend verlieren oder (schlimmer) den
   * Server die gesamte IDS-Datei ablehnen lassen. Die Auswahl wird deshalb auf "Erforderlich"
   * fixiert, statt eine Einstellung anzubieten, die nie ankommt.
   */
  get necessityLockedToRequired(): boolean {
    return this.facet?.type === 'entity' || this.facet?.type === 'entityGroup';
  }

  /** Necessity-Auswahl für das Template: bei Entity-Facetten immer "required", sonst editierbar. */
  get necessityValue(): NecessityType | undefined {
    return this.necessityLockedToRequired ? 'required' : this.facet.metaNecessity;
  }

  set necessityValue(value: NecessityType | undefined) {
    if (this.necessityLockedToRequired) return;
    this.facet.metaNecessity = value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Normalisiert auch importierte/ältere Facetten, deren metaNecessity noch "optional" oder
    // "prohibited" ist (z.B. aus einer Zeit vor diesem Fix) - sonst zeigt necessityValue zwar
    // "Erforderlich", aber buildRequirementsSummary() läse weiterhin den alten, abweichenden Wert.
    if (changes['facet'] && this.necessityLockedToRequired && this.facet.metaNecessity !== 'required') {
      this.facet.metaNecessity = 'required';
    }
  }

  /** Per-field temp input for adding enum values */
  enumInputs = new Map<IDSValueField, string>();

  getEnumInput(field: IDSValueField): string {
    return this.enumInputs.get(field) ?? '';
  }

  setEnumInput(field: IDSValueField, value: string): void {
    this.enumInputs.set(field, value);
  }

  /** Controls visibility of the propertyDataType suggestion dropdown */
  showPropertyDataTypeDropdown = false;

  /**
   * Vorschläge für „Datentyp Eigenschaftswert": zuerst die Datentypengruppen (#2312), dann die
   * IFC-Datentypen.
   *
   * Die Gruppen stehen oben, weil sie in der Regel gemeint sind — „irgendeine Zahl" ist häufiger
   * gefordert als genau `IfcAreaMeasure`. Gefiltert wird über Anzeigename **und** Token, damit
   * sowohl „Zahl" als auch „ZAHL" trifft.
   */
  get filteredDataTypeGroups(): IfcDataTypeGroup[] {
    const current = this.facet?.propertyDataType?.trim().toLowerCase() ?? '';
    if (!current) return IFC_DATA_TYPE_GROUPS;
    return IFC_DATA_TYPE_GROUPS.filter(group =>
      group.label.toLowerCase().includes(current) || group.token.toLowerCase().includes(current));
  }

  /**
   * Returns filtered IFC data types based on current input.
   *
   * Quelle sind die im IFC-Schema bekannten Datentypen, nicht mehr nur die sechs häufigsten:
   * `IfcCountMeasure` und die übrigen Maße fehlten in der Liste und mussten von Hand getippt
   * werden. Ohne Eingabe bleibt es bei der kurzen, kuratierten Liste — 126 Vorschläge auf einmal
   * sind keine Hilfe.
   */
  get filteredPropertyDataTypes(): string[] {
    const current = this.facet?.propertyDataType?.trim().toLowerCase() ?? '';
    if (!current) return PROPERTY_DATA_TYPES;
    return knownDataTypes(this.ifcVersions)
      .filter(name => name.toLowerCase().includes(current));
  }

  /** Die aktuell gewählte Datentypengruppe, oder `null` bei einem echten IFC-Typ. */
  get selectedDataTypeGroup(): IfcDataTypeGroup | null {
    return dataTypeGroupOf(this.facet?.propertyDataType);
  }

  /** Anzahl der IFC-Datentypen einer Gruppe, für die Hinweistexte. */
  dataTypeGroupCount(group: IfcDataTypeGroup): number {
    return dataTypeGroupMembers(group, this.ifcVersions).length;
  }

  /** Tooltip-Kopfzeile zu einer Gruppe (fett): Kurzerklärung plus Trefferzahl. */
  dataTypeGroupTooltipTitle(group: IfcDataTypeGroup): string {
    return dataTypeGroupTooltipTitle(group, this.ifcVersions);
  }

  /** Tooltip-Detailzeile zu einer Gruppe (normal): die enthaltenen IFC-Datentypen. */
  dataTypeGroupMemberList(group: IfcDataTypeGroup): string {
    return dataTypeGroupMemberList(group, this.ifcVersions);
  }

  /**
   * Übernimmt eine Gruppe als Datentyp: im Feld erscheint der lesbare Name ("Text"), nicht das
   * XML-Token ("TEXT") - facetToXml schreibt beim Speichern wieder groß.
   */
  selectDataTypeGroup(group: IfcDataTypeGroup): void {
    this.facet.propertyDataType = group.label;
    this.showPropertyDataTypeDropdown = false;
    this.emitChange();
  }

  selectPropertyDataType(value: string) {
    this.facet.propertyDataType = value;
    this.showPropertyDataTypeDropdown = false;
    this.emitChange();
  }

  hidePropertyDataTypeDropdown() {
    // Delay to allow click on dropdown item
    setTimeout(() => this.showPropertyDataTypeDropdown = false, 200);
  }

  // ── Entitätengruppen (#2310) ──────────────────────────────────────────────────────────────

  /**
   * Die aktuell erkannte Gruppe des Entitätsnamens, oder `null` bei freier Auswahl.
   *
   * Bewusst aus den Werten **abgeleitet** und nicht als eigenes Feld gespeichert: im IDS-XML steht
   * nur die Aufzählung. Ein zusätzliches Flag würde beim Laden einer fremden IDS fehlen und beim
   * Bearbeiten der Badges auseinanderlaufen.
   */
  get selectedEntityGroup(): IfcEntityGroup | null {
    const field = this.facet?.entityName;
    if (!field || field.dataType !== 'Aufzählung') return null;
    return matchEntityGroup(field.enumValues ?? [], this.ifcVersions);
  }

  /** Wert für die Gruppen-Auswahl: Schlüssel der erkannten Gruppe, sonst leer. */
  get entityGroupValue(): string {
    return this.selectedEntityGroup?.key ?? '';
  }

  /**
   * Setzt die Gruppe: füllt die Aufzählung mit ihren IFC-Klassen.
   *
   * Ein leerer Schlüssel setzt auf die freie Auswahl zurück und **leert** die Werte — sonst blieben
   * die 172 Klassen von „Alle Elemente" als scheinbar handverlesene Liste stehen.
   */
  onEntityGroupChange(key: string): void {
    const field = this.facet.entityName;
    const group = IFC_ENTITY_GROUPS.find(entry => entry.key === key);

    if (!group) {
      field.enumValues = [];
      this.emitChange();
      return;
    }

    field.dataType = 'Aufzählung';
    field.simpleValue = '';
    field.enumValues = groupMembers(group, this.ifcVersions);
    this.emitChange();
  }

  // ── Subklassen mitprüfen (#2311) ──────────────────────────────────────────────────────────

  /**
   * Lässt sich „Subklassen mitprüfen" auf dieses Feld anwenden?
   *
   * Nein bei einer aktiven Entitätengruppe — die ist selbst schon ein vollständiger
   * Subtyp-Abschluss, das Häkchen hätte dort keine Wirkung. Und nein bei Muster, Grenzen oder
   * Länge: die nennen keine IFC-Klassen, aus denen ein Abschluss zu bilden wäre.
   */
  get canIncludeSubtypes(): boolean {
    const dataType = this.facet?.entityName?.dataType;
    if (dataType !== 'Wert' && dataType !== 'Aufzählung') return false;
    return this.selectedEntityGroup === null;
  }

  /** Erklärt die Checkbox — inklusive des Grunds, wenn sie nicht wählbar ist. */
  get includeSubtypesTooltip(): string {
    if (this.selectedEntityGroup) {
      return 'Eine Entitätengruppe enthält die Subklassen bereits.';
    }
    if (!this.canIncludeSubtypes) {
      return 'Nur bei „Wert" oder „Aufzählung" möglich – ein Muster nennt keine IFC-Klassen.';
    }
    const preview = this.subtypePreview;
    return preview.length === 0
      ? 'Prüft zusätzlich alle Subklassen mit, z. B. IfcWallStandardCase zu IfcWall. '
        + 'Die Regel wird beim Speichern um die Klassen ergänzt.'
      : `Zusätzlich geprüft (${preview.length}): ${entityListTooltip(preview)}`;
  }

  /** Die Subklassen, die beim Speichern ergänzt würden. */
  get subtypePreview(): string[] {
    if (!this.canIncludeSubtypes) return [];
    const field = this.facet.entityName;
    const named = field.dataType === 'Wert'
      ? [field.simpleValue ?? '']
      : (field.enumValues ?? []);
    const found = new Set<string>();
    for (const parent of named.map(name => (name ?? '').trim()).filter(Boolean)) {
      for (const subtype of subtypesOf(parent, this.ifcVersions)) found.add(subtype);
    }
    return [...found].sort();
  }

  toggleIncludeSubtypes(): void {
    // Kein readOnly-Guard: die Facetten-Komponente kennt keinen View-only-Modus, das regelt der
    // Editor eine Ebene höher (er zeigt dort kein Speichern an). Genauso wie bei allen anderen
    // Feldern dieser Komponente.
    if (!this.canIncludeSubtypes) return;
    this.facet.includeSubtypes = !this.facet.includeSubtypes;
    this.emitChange();
  }

  /**
   * Sollen die Einzelwerte einer Aufzählung als Badges erscheinen?
   *
   * Bei einer aktiven Entitätengruppe nicht: „Alle Elemente" sind über 170 IFC-Klassen, die als
   * Badge-Teppich das Formular unbrauchbar machen. Sie stehen im Tooltip der Gruppen-Badge. Für
   * jedes andere Feld bleibt es bei der bisherigen Darstellung.
   */
  showEnumBadges(field: IDSValueField): boolean {
    if (field !== this.facet?.entityName) return true;
    return this.selectedEntityGroup === null;
  }

  /** Tooltip der Gruppen-Auswahl bzw. -Badge: welche IFC-Klassen stecken dahinter. */
  entityGroupTooltip(group: IfcEntityGroup): string {
    const members = groupMembers(group, this.ifcVersions);
    return `${group.hint} (${members.length}): ${entityListTooltip(members)}`;
  }

  toggleExpanded() {
    this.facet.expanded = !this.facet.expanded;
    this.emitChange();
  }

  onTypeChange(newType: FacetType) {
    const updated = createDefaultFacet(newType);
    updated.id = this.facet.id;
    updated.expanded = this.facet.expanded;
    if (this.isRequirement) updated.metaNecessity = this.facet.metaNecessity;
    Object.assign(this.facet, updated);
    // Wechsel auf Entität: necessityValue-Setter greift hier nicht (kein neues Input, derselbe
    // Facet-Objektverweis) - ohne dies bliebe eine zuvor gewählte "Optional"/"Verboten"-Necessity
    // stehen, obwohl das Feld jetzt gesperrt ist und beim Export ohnehin ignoriert würde.
    if (this.necessityLockedToRequired) this.facet.metaNecessity = 'required';
    this.emitChange();
  }

  onDataTypeChange(field: IDSValueField, newType: DataType) {
    field.dataType = newType;
    // Reset values when switching
    field.simpleValue = '';
    field.enumValues = [];
    field.rangeMin = '';
    field.rangeMax = '';
    field.pattern = '';
    field.lengthValue = '';
    field.lengthMin = '';
    field.lengthMax = '';
    this.emitChange();
  }

  addEnumValue(field: IDSValueField, inputValue: string) {
    if (inputValue?.trim()) {
      field.enumValues.push(inputValue.trim());
      this.enumInputs.set(field, '');
      this.emitChange();
    }
  }

  removeEnumValue(field: IDSValueField, index: number) {
    field.enumValues.splice(index, 1);
    this.emitChange();
  }

  emitChange() {
    this.facetChange.emit(this.facet);
  }

  get facetLabel(): string {
    const label = FACET_TYPE_LABELS[this.facet.type];
    let value = '...';
    switch (this.facet.type) {
      case 'entity': value = getValueFieldDisplay(this.facet.entityName); break;
      case 'entityGroup': value = this.selectedEntityGroup?.label ?? getValueFieldDisplay(this.facet.entityName); break;
      case 'attribute': value = getValueFieldDisplay(this.facet.attributeName); break;
      case 'property': value = getValueFieldDisplay(this.facet.propertyName); break;
      case 'classification': value = getValueFieldDisplay(this.facet.classificationSystem); break;
      case 'material': value = getValueFieldDisplay(this.facet.materialName); break;
      case 'partOf': value = getValueFieldDisplay(this.facet.partOfEntityName); break;
    }
    return `${label}: ${value}`;
  }

  /** Template helper to check if a value field is empty (for errorCondition binding). */
  isFieldEmpty(field: IDSValueField): boolean {
    return isValueFieldEmpty(field);
  }

  get propertySetDisplayValue(): string {
    const v = this.facet.propertySet?.simpleValue;
    return v === '.*' ? '' : (v ?? '');
  }

  onPropertySetChange(value: string): void {
    this.facet.propertySet.simpleValue = (!value || !value.trim()) ? '.*' : value;
    this.emitChange();
  }
}

